import React from 'react';
import { ToolResultView } from '../../../ToolExecution/ToolResultView';
import { getRenderer } from '../../../tool-results/registry';
import type {
  ToolResultBlock,
  ToolUseBlock,
} from '../../../../store/chatStore';
import { safeJsonParse } from '../../../../../../utils/jsonRepairUtils';
import { ErrorBoundary } from '../ErrorBoundary';
import { useDebouncedValue } from '../useDebouncedValue';

interface MarkdownVulnerabilitiesProps {
  code: string;
}

interface VulnerabilitiesToolResultContent {
  data: Record<string, unknown>[];
  meta?: {
    page?: number;
    perPage?: number;
    total?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  };
}

const MARKDOWN_VULNERABILITIES_TOOL_USE_ID =
  'markdown-vulnerabilities-tool-use';

const Skeleton = () => {
  return (
    <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
      <div className="animate-pulse space-y-3">
        <div className="h-8 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-6 w-full rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-6 w-11/12 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    </div>
  );
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getVulnerabilitiesToolResultContent(
  value: unknown,
): VulnerabilitiesToolResultContent | undefined {
  if (!isRecord(value) || !Array.isArray(value.data)) {
    return undefined;
  }

  const content: VulnerabilitiesToolResultContent = {
    data: value.data as Record<string, unknown>[],
  };

  if (isRecord(value.meta)) {
    content.meta = value.meta as VulnerabilitiesToolResultContent['meta'];
  }

  return content;
}

function extractVulnerabilitiesPayload(parsed: unknown): {
  content: VulnerabilitiesToolResultContent;
} | null {
  if (Array.isArray(parsed)) {
    return {
      content: {
        data: parsed as Record<string, unknown>[],
        meta: {
          page: 1,
          perPage: parsed.length,
          total: parsed.length,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      },
    };
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const directContent = getVulnerabilitiesToolResultContent(parsed);
  if (directContent) {
    return {
      content: directContent,
    };
  }

  const wrappedContent = getVulnerabilitiesToolResultContent(parsed.content);
  if (wrappedContent) {
    return {
      content: wrappedContent,
    };
  }

  return null;
}

const VulnerabilitiesRenderer = ({ code }: { code: string }) => {
  const parsed = safeJsonParse<unknown>(code, 'vulnerabilities markdown');
  const payload = extractVulnerabilitiesPayload(parsed);

  if (!payload) {
    return <Skeleton />;
  }

  const toolUse: ToolUseBlock = {
    type: 'toolUse',
    id: MARKDOWN_VULNERABILITIES_TOOL_USE_ID,
    name: 'listVulnerabilities',
    input: {},
    meta: {
      humanReadableName: 'Plerion vulnerabilities',
      source: 'plerion',
    },
  };

  const result: ToolResultBlock = {
    type: 'toolResult',
    toolUseId: MARKDOWN_VULNERABILITIES_TOOL_USE_ID,
    toolName: 'listVulnerabilities',
    content: JSON.stringify(payload.content),
    isError: false,
  };

  return (
    <div className="not-prose">
      <ToolResultView
        result={result}
        toolUse={toolUse}
        getRenderer={getRenderer}
      />
    </div>
  );
};

export const MarkdownVulnerabilities: React.FC<
  MarkdownVulnerabilitiesProps
> = ({ code }) => {
  const debouncedCode = useDebouncedValue(code, 300, 500);

  return (
    <div className="not-prose mb-4">
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error(
            'Vulnerabilities markdown rendering error:',
            error,
            errorInfo,
          );
        }}
        resetKey={debouncedCode}
        fallback={<Skeleton />}
      >
        <VulnerabilitiesRenderer code={debouncedCode} />
      </ErrorBoundary>
    </div>
  );
};

export default MarkdownVulnerabilities;
